import { debug } from "./config.js";
import type { ListConfig, ListResponse, ListState } from "./types.js";

/**
 * The one place that knows the list is served over HTTP.
 *
 * Everything above it deals only in `ListState -> ListResponse`, so a future
 * client-side mode (fetch the whole collection once, filter in memory) can be
 * swapped in without touching a single `data-slater-*` attribute.
 */
export interface DataSource {
  query(state: ListState, offset: number, limit: number): Promise<ListResponse>;
  /**
   * Cancels any in-flight request without starting a new one. Supersession
   * handles the typing case; this covers going idle — a cleared search box must
   * not be repopulated by a response that was already on the wire.
   */
  abort?(): void;
}

/** Bounded LRU. A visitor toggling filters back and forth re-reads, not re-fetches. */
export class ResponseCache {
  private readonly entries = new Map<string, ListResponse>();
  constructor(private readonly maxSize = 50) {}

  get(key: string): ListResponse | undefined {
    const hit = this.entries.get(key);
    if (!hit) return undefined;
    this.entries.delete(key);
    this.entries.set(key, hit);
    return hit;
  }

  set(key: string, value: ListResponse): void {
    if (this.entries.size >= this.maxSize) {
      const oldest = this.entries.keys().next().value;
      if (oldest !== undefined) this.entries.delete(oldest);
    }
    this.entries.set(key, value);
  }
}

export class HttpError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export class ServerDataSource implements DataSource {
  private readonly cache = new ResponseCache();
  private inFlight: AbortController | null = null;

  constructor(private readonly config: ListConfig) {}

  private url(state: ListState, offset: number, limit: number): string {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    if (state.q) params.set("q", state.q);
    if (state.sort) params.set("sort", state.sort);
    if (this.config.locale) params.set("locale", this.config.locale);
    if (this.config.facets.length) params.set("facets", this.config.facets.join(","));
    if (this.config.expand.length) params.set("expand", this.config.expand.join(","));
    if (state.q && this.config.searchFields.length) {
      params.set("searchFields", this.config.searchFields.join(","));
    }

    // The base filter is always applied and cannot be cleared by the visitor. A
    // user filter on the same field would combine with it (AND), so a base filter
    // should use a field the visitor can't also control (e.g. a page-scope `bu`).
    const filters: Record<string, string[]> = { ...this.config.baseFilter };
    for (const [field, values] of Object.entries(state.filters)) {
      filters[field] = [...(filters[field] ?? []), ...values];
    }

    for (const [field, values] of Object.entries(filters)) {
      for (const value of values) params.append(`filter[${field}]`, value);
      const mode = state.matchByField[field];
      if (mode) params.set(`match[${field}]`, mode);
    }

    // The server picks the staging or production index from ?host -> Origin ->
    // Referer. Sending it explicitly makes the choice deterministic and puts it
    // in the URL, so any cache keyed on the URL stays correct.
    params.set("host", window.location.hostname);

    const { endpoint, siteId, collection, group } = this.config;
    // A group list is the same contract on a different resource: the merged
    // /:siteId/group/:slug endpoint accepts the identical query vocabulary.
    const resource = group
      ? `group/${encodeURIComponent(group)}`
      : encodeURIComponent(collection);
    return `${endpoint}/${encodeURIComponent(siteId)}/${resource}?${params}`;
  }

  async query(state: ListState, offset: number, limit: number): Promise<ListResponse> {
    const url = this.url(state, offset, limit);

    const cached = this.cache.get(url);
    if (cached) {
      debug("cache hit", url);
      return cached;
    }

    // A new query supersedes whatever is in flight — the user has typed another
    // character or changed a filter, and the old response is already stale.
    this.inFlight?.abort();
    const controller = new AbortController();
    this.inFlight = controller;

    debug("fetch", url);
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { accept: "application/json" },
    });

    if (!response.ok) {
      throw new HttpError(
        response.status,
        response.status === 404
          ? this.config.group
            ? `Group "${this.config.group}" was not found for site ${this.config.siteId}`
            : `Collection "${this.config.collection}" is not enabled for site ${this.config.siteId}`
          : response.status === 402
            ? `This site's Slater plan does not cover collection data (402)`
            : `Request failed with ${response.status}`,
      );
    }

    const body = (await response.json()) as ListResponse;
    this.cache.set(url, body);
    return body;
  }

  abort(): void {
    this.inFlight?.abort();
    this.inFlight = null;
  }
}
